import { toJS } from 'mobx';
import Api, { SitemapParams } from '../../../services/Api';
import ITreeNode from '../../../components/tree/ITreeNode';
import TreeLinkedList from './TreeLinkedList';
import AbstractSessionModel from '../../../models/AbstractSessionModel';
import Observable from '../../../decorators/Observable';
import Action from '../../../decorators/Action';
import StoreFieldInSession from '../../../decorators/StoreFieldInSession';
import SiteMapWizardData, { SiteMapWizardDataType } from './SiteMapWizardData';
import { IWizardDataModel } from '../../../models/IWizardDataModel';
import WizardSessionData from '../../../services/WizardSessionData';

export interface ISiteMapTreeData {
  id: string | number;
  parent: string | number;
  canDrag: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canHaveChildren: boolean;
  isPageDefault: boolean;
  isCategoryBasedPage: boolean;
  isDropdownPage: boolean;
  isCustomPage?: boolean;
  canBeMarkedAsImportant?: boolean;
  isEndOfCustomPages?: boolean;
  type: string;
  areas?: {
    languageCode: string;
    name: string;
    text: string;
  }[];
  data: {
    path: string;
    description: string;
    pageName: string;
    keywords: string[];
  };
}

class SiteMapModel extends AbstractSessionModel implements IWizardDataModel {
  // Holds a snapshot of the treeSiteMap when Manage Sitemap is opened
  private _treeSiteMapSnapshot: ITreeNode[] | null = null;
  /**
   * this stores the data coming from the API
   */
  @StoreFieldInSession()
  @Observable()
  siteMap: ISiteMapTreeData[] = [];

  /**
   * this stores the data in the format that the tree component expects
   */
  @StoreFieldInSession()
  @Observable()
  treeSiteMap: ITreeNode[] = [];

  @StoreFieldInSession({
    reactOn: (value) => {
      // basically, we react only on changes of the treeLinkList.head value.
      // when the user change the structure of the tree, we call updateSitemap function
      // and the treeLinkList.head value is changed,
      // which triggers the re-render of the tree component
      // and fire reaction in AbstractSessionModel
      return value?.toJSON() || null;
    },
    serialize: (value: any) => {
      return value?.serialize() || JSON.stringify([]);
    },
    deserialize(value: any) {
      let object = JSON.parse(value);
      let treeLinkList = new TreeLinkedList();
      treeLinkList.deserialize(object);
      return treeLinkList;
    }
  })
  @Observable()
  treeLinkList = new TreeLinkedList();

  @Observable()
  isAddPageModalOpened = false;

  @Observable()
  isEditPageModalOpened = false;

  @Observable()
  isDokuModalOpened = false;

  @Observable()
  editedPageNode: ITreeNode = null;

  @Observable()
  parentNodeForNewPage: ITreeNode = null;

  @Observable()
  parentInsertionReference: ITreeNode = null;

  @Observable()
  siteMapFething = false;

  @Observable()
  externalSiteMapFething = false;

  @Observable()
  siteMapWizardData = new SiteMapWizardData();

  @StoreFieldInSession()
  @Observable()
  oldWebsiteImages = [];

  @Observable()
  websiteUrl = '';

  @Observable()
  isManagePagesVisible = false;

  @Observable()
  isSaving = false;

  public readonly SITEMAP_ROOT_INTERNAL = 'rootSiteTree';

  public readonly SITEMAP_ROOT_EXTERNAL = 'rootExistingSiteTree';

  constructor() {
    super();

    super.initFields();
  }

  @Action()
  public closeEditPageModal() {
    this.isEditPageModalOpened = false;
    this.editedPageNode = null;
    this.parentNodeForNewPage = null;
  }

  @Action()
  public openEditPageModal(node: ITreeNode) {
    this.isEditPageModalOpened = true;
    this.editedPageNode = node;
    this.parentNodeForNewPage = null;
    this.parentInsertionReference = null;
  }

  @Action()
  public openAddPageModal(parentNode: ITreeNode) {
    this.isEditPageModalOpened = true;
    this.editedPageNode = null;
    this.parentNodeForNewPage = parentNode;
    this.parentInsertionReference = null;
  }

  @Action()
  public openAddPageModalAsSibling(node: ITreeNode) {
    this.isEditPageModalOpened = true;
    this.editedPageNode = null;
    // Set parent to the parent of the clicked node (same level)
  // find parent node object by id (node.parent may be an id)
  const parentObj = this.treeSiteMap.find((n) => n.id === node.parent) || null;
  this.parentNodeForNewPage = parentObj;
    // Remember the node after which a new node should be inserted
    this.parentInsertionReference = node;
  }

  @Action()
  public closeDokuModal() {
    this.isDokuModalOpened = false;
  }

  @Action()
  public openDokuModal() {
    this.isDokuModalOpened = true;
  }

  @Action()
  public openManagePages() {
    this.isManagePagesVisible = true;
    // Take a deep snapshot of the current treeSiteMap for cancel revert
    this._treeSiteMapSnapshot = JSON.parse(JSON.stringify(this.treeSiteMap));
    document.body.style.overflow = 'hidden';
    const ctaWrapper = document.querySelector('.preview-cta-wrapper');
    const fixedButtons = document.querySelector('.fixed-buttons-wrapper');
    if (ctaWrapper) {
      (ctaWrapper as HTMLElement).style.display = 'none';
    }
    if (fixedButtons) {
      (fixedButtons as HTMLElement).style.display = 'none';
    }
  }

  @Action()
  public closeManagePages() {
    // If a snapshot exists, restore it (for Cancel action)
    if (this._treeSiteMapSnapshot) {
      this.treeSiteMap = JSON.parse(JSON.stringify(this._treeSiteMapSnapshot));
      this._treeSiteMapSnapshot = null;
    }
    this.isManagePagesVisible = false;
    document.body.style.overflow = '';
    const ctaWrapper = document.querySelector('.preview-cta-wrapper');
    const fixedButtons = document.querySelector('.fixed-buttons-wrapper');
    if (fixedButtons) {
      (fixedButtons as HTMLElement).style.display = '';
    }
    if (ctaWrapper) {
      (ctaWrapper as HTMLElement).style.display = '';
    }
  }

  // Call this after a successful save to clear the snapshot
  public clearManagePagesSnapshot() {
    this._treeSiteMapSnapshot = null;
  }

  @Action()
  public setSaving(value: boolean) {
    this.isSaving = value;
  }

  private isPageExternal(node: ISiteMapTreeData) {
    if (node.parent === this.SITEMAP_ROOT_EXTERNAL) {
      return true;
    }

    for (let i = 0; i < this.siteMap.length; i++) {
      if (this.siteMap[i].id === node.parent) {
        return this.isPageExternal(this.siteMap[i]);
      }
    }

    return false;
  }

  private isTreeNodeExternal(node: ITreeNode, treeSiteMap = null) {
    if (node.parent === this.SITEMAP_ROOT_EXTERNAL) {
      return true;
    }

    let searchIn = treeSiteMap || this.treeSiteMap;

    for (let i = 0; i < searchIn.length; i++) {
      if (searchIn[i].id === node.parent) {
        return this.isTreeNodeExternal(searchIn[i]);
      }
    }

    return false;
  }

  private findFirstRootNode() {
    for (let i = 0; i < this.siteMap.length; i++) {
      if (this.siteMap[i].parent === this.SITEMAP_ROOT_INTERNAL) {
        return this.siteMap[i];
      }
    }

    return null;
  }

  private findIndexOfEndCustomPages() {
    for (let i = 0; i < this.siteMap.length; i++) {
      if (
        this.siteMap[i].parent === this.SITEMAP_ROOT_INTERNAL &&
        this.siteMap[i].isEndOfCustomPages === true
      ) {
        return i;
      }
    }

    return -1;
  }

  private findNode(id: string | number) {
    for (let i = 0; i < this.treeSiteMap.length; i++) {
      if (this.treeSiteMap[i].id === id) {
        return this.treeSiteMap[i];
      }
    }

    return null;
  }

  /**
   * @TODO - add .hasChildren to ITreeNode instead of using this method
   *
   * @param node
   */
  @Action()
  public hasChildren(node: ITreeNode) {
    return (
      this.treeSiteMap.filter((item) => item.parent === node.id).length > 0
    );
  }

  private populateTreeLinkedList(
    treeData: ITreeNode[],
    treeLinkList: TreeLinkedList
  ) {
    const rootSiteTreeNodes = treeData.filter(
      (node) => node.parent === this.SITEMAP_ROOT_INTERNAL
    );

    let recur = (node: ITreeNode) => {
      treeLinkList.addNode(node);

      if (node.data.isPageDefault) {
        this.confirmNodeKeyword(node);
      }

      let children = treeData.filter((child) => child.parent === node.id);
      children.forEach((child) => {
        recur(child);
      });
    };

    rootSiteTreeNodes.forEach((node) => {
      recur(node);
    });
  }

  @Action()
  private loadSiteMap(
    siteMap: ISiteMapTreeData[],
    origin: 'external' | 'internal' = 'internal'
  ) {
    // Avoid duplicating nodes when loadSiteMap is called multiple times.
    // Keep existing nodes and only append incoming nodes that don't already exist by id.
    const existingSiteMapIds = new Set(this.siteMap.map((s) => s.id));
    const uniqueSiteMapIncoming = siteMap.filter((s) => !existingSiteMapIds.has(s.id));

    this.siteMap = [...this.siteMap, ...uniqueSiteMapIncoming];

    // @TODO - optimize this to use Map instead of Array
    const existingTreeNodeIds = new Set(this.treeSiteMap.map((n) => n.id));
    const uniqueTreeNodes = siteMap
      .filter((n) => !existingTreeNodeIds.has(n.id))
      .map((node) => {
        return {
          id: node.id,
          parent: node.parent,
          text: node.data.pageName,
          droppable: node.canHaveChildren,

          // @TODO - optimize this
          isNodeCurrentlyOpenForEdit: this.findFirstRootNode()?.id === node.id,
          hasPageChildren:
            this.siteMap.filter((item) => item.parent === node.id).length > 0,

          data: {
            ...node.data,
            canDrag: node.canDrag,
            canEdit: node.isPageDefault ? false : node.canEdit,
            canDelete: node.canDelete,
            // @TODO - optimize this
            isPageExternal: this.isPageExternal(node),
            isPageDefault: node.isPageDefault,
            isCategoryBasedPage: node.isCategoryBasedPage,
            isCustomPage: false,
            type: node.type,
            isDropdownPage: node.isDropdownPage,
            isEndOfCustomPages: node.isEndOfCustomPages,

            customKeywords: [],
            deselectedKeywords: [],
            origin
          }
        };
      });

    this.treeSiteMap = [...this.treeSiteMap, ...uniqueTreeNodes];
  }

  /**
   * This method is called when the sitemap tree structure is updated in the UI tree component.
   *
   * @param tree
   */
  @Action()
  public updateSitemap(tree: ITreeNode[]) {
    this.treeSiteMap = tree.map((node) => {
      let data = node.data;

      // check if the origin is external and the node is not external anymore
      if (
        node.data.origin === 'external' &&
        this.isTreeNodeExternal(node, tree) === false &&
        node.id !== this.SITEMAP_ROOT_EXTERNAL
      ) {
        data = {
          ...data,
          canEdit: true,
          canDelete: true,
          canHaveChildren: true,
          origin: 'internal',
          movedFromExternal: true  // Flag this page as moved from external
        };
        node.droppable = true;
     
      }

      return {
        ...node,
        data,
        // @TODO - optimize this
        hasPageChildren:
          tree.filter((item) => item.parent === node.id).length > 0
      };
    });

    this.siteMap = tree.map((node) => {
      return {
        id: node.id,
        parent: node.parent,
        canDrag: node.data.canDrag,
        canEdit: node.data.canEdit,
        canDelete: node.data.canDelete,
        canHaveChildren: node.droppable,
        isPageDefault: node.data.isPageDefault,
        isCategoryBasedPage: node.data.isCategoryBasedPage,
        isCustomPage: node.data.isCustomPage,
        type: node.data.type,
        isDropdownPage: node.data.isDropdownPage,
        isEndOfCustomPages: node.data.isEndOfCustomPages,

        data: {
          path: node.data.path,
          description: node.data.description,
          pageName: node.text,
          keywords: node.data.keywords,
          deselectedKeywords: node.data.deselectedKeywords
        }
      };
    });

    let currentPointerId = this.getCurrentOpenForEditNodeId();

    this.treeLinkList.reset();

    this.populateTreeLinkedList(tree, this.treeLinkList);
    this.treeLinkList.resetPointer();

    currentPointerId && this.treeLinkList.setCurrentPointer(currentPointerId);
  }

  getCurrentOpenForEditNodeId() {
    return this.treeSiteMap.find((node) => node.isNodeCurrentlyOpenForEdit)?.id;
  }

  @Action()
  public makeNextNodeOpenForEdit() {
    let treeLink = this.treeLinkList.next();

    if (this.findNode(treeLink?.getId())?.isNodeCurrentlyOpenForEdit === true) {
      treeLink = this.treeLinkList.next();
    }

    while (
      treeLink &&
      this.findNode(treeLink?.getId())?.data?.isPageDefault === true
    ) {
      treeLink = this.treeLinkList.next();
    }

    const id = treeLink?.getId();

    for (let i = 0; i < this.treeSiteMap.length; i++) {
      if (this.treeSiteMap[i].id === id) {
        this.treeSiteMap[i].isNodeCurrentlyOpenForEdit = true;
      } else {
        this.treeSiteMap[i].isNodeCurrentlyOpenForEdit = false;
      }
    }

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  @Action()
  public makeNodeOpenForEdit(node: ITreeNode) {
    for (let i = 0; i < this.treeSiteMap.length; i++) {
      if (this.treeSiteMap[i].id === node.id) {
        this.treeSiteMap[i].isNodeCurrentlyOpenForEdit = true;
      } else {
        this.treeSiteMap[i].isNodeCurrentlyOpenForEdit = false;
      }
    }

    // set current pointer to the node
    this.treeLinkList.setCurrentPointer(node.id);

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  @Action()
  public deleteExternalNode(node: ITreeNode) {
    let recur = (_node: ITreeNode) => {
      // move only the external nodes to level 0 (root) of the external tree
      if (_node.data.origin === 'external') {
        _node.parent = this.SITEMAP_ROOT_EXTERNAL;
        _node.data.canEdit = false;
        _node.data.canDelete = false;
        _node.data.canHaveChildren = false;
        _node.droppable = false;
      }

      let children = this.treeSiteMap.filter(
        (item) => item.parent === _node.id
      );

      children.forEach((child) => {
        recur(child);
      });

      if (_node.data.origin !== 'external') {
        this.treeSiteMap = this.treeSiteMap.filter(
          (item) => item.id !== _node.id
        );
      }
    };

    recur(node);

    this.treeSiteMap = [...this.treeSiteMap];
  }

  /**
   * Delete a node and its children from the tree.
   *
   * @param node
   * @returns
   */
  @Action()
  public deleteNode(node: ITreeNode) {
    if (node.data.origin === 'external') {
      this.deleteExternalNode(node);
      return;
    }

    let recur = (node) => {
      let children = this.treeSiteMap.filter((item) => item.parent === node.id);

      children.forEach((child) => {
        recur(child);
      });

      // move only the external nodes to level 0 (root) of the external tree
      if (node.data.origin === 'external') {
        node.parent = this.SITEMAP_ROOT_EXTERNAL;
        node.data.canEdit = false;
        node.data.canDelete = false;
        node.data.canHaveChildren = false;
        node.droppable = false;
      } else {
        this.treeSiteMap = this.treeSiteMap.filter(
          (item) => item.id !== node.id
        );
      }
    };

    recur(node);

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  /**
   * Delete a node and move its children to the parent of the node.
   *
   * @param node
   */
  @Action()
  public deleteNodeAndMoveSubpages(node: ITreeNode) {
    let parent = node.parent;

    const directChildren = [];
    const directChildrenIndexes = [];

    // move the children to the parent of the node
    this.treeSiteMap.forEach((item) => {
      if (item.parent === node.id) {
        item.parent = parent;
        // collect the direct children
        directChildren.push(item);
        directChildrenIndexes.push(this.treeSiteMap.indexOf(item));
      }
    });

    this.treeSiteMap = this.treeSiteMap.filter((item, index) => {
      return !directChildrenIndexes.includes(index);
    });

    let currentIndexOfDeletedNode = this.treeSiteMap.findIndex(
      (item) => item.id === node.id
    );

    // split the treeSiteMap into two parts
    // the first part is the nodes before the deleted node
    // the second part is the nodes after the deleted node
    let firstPart = this.treeSiteMap.slice(0, currentIndexOfDeletedNode);

    // we dont want to delete the node if it is external, in this case we want to move it to the root of the external tree
    let secondIndex =
      node.data.origin === 'external'
        ? currentIndexOfDeletedNode
        : currentIndexOfDeletedNode + 1;

    let secondPart = this.treeSiteMap.slice(
      secondIndex,
      this.treeSiteMap.length
    );

    // merge the two parts with the direct children
    this.treeSiteMap = [...firstPart, ...directChildren, ...secondPart];

    if (node.data.origin === 'external') {
      node.parent = this.SITEMAP_ROOT_EXTERNAL;
      node.data.canEdit = false;
      node.data.canDelete = false;
      node.data.canHaveChildren = false;
      node.droppable = false;
    }
  }

  @Action()
  public addKeyword(node, keyword) {
    let index = node.data.deselectedKeywords.indexOf(keyword);

    if (index !== -1) {
      node.data.deselectedKeywords.splice(index, 1);
    }

    if (node.data.keywords.indexOf(keyword) === -1) {
      node.data.keywords.push(keyword);
    }

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  @Action()
  public updateNodeKeywords(nodeId, customKeywords, deselectedKeywords) {
    let node = this.treeSiteMap.find((item) => item.id === nodeId);

    node.data.customKeywords = customKeywords;
    node.data.deselectedKeywords = deselectedKeywords;

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  @Action()
  public confirmNodeKeyword(node) {
    // find node in the treeSiteMap
    let treeNode = this.treeSiteMap.find((item) => item.id === node.id);

    treeNode.isNodeKeywordConfirmed = true;

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  @Action()
  public isAllKeywordsConfirmed() {
    let allKeywordsConfirmed = true;

    this.treeSiteMap.forEach((node) => {
      if (this.isTreeNodeExternal(node)) {
        return;
      }

      if (
        node.data.keywords.length > 0 &&
        node.isNodeKeywordConfirmed !== true
      ) {
        allKeywordsConfirmed = false;
      }
    });

    return allKeywordsConfirmed;
  }

  @Action()
  public getAllInternalPageNodes() {
    let tree = this.treeSiteMap.filter(
      (node) => !this.isTreeNodeExternal(node)
    );

    const treeLinkList = new TreeLinkedList();
    this.populateTreeLinkedList(tree, treeLinkList);
    treeLinkList.resetPointer();

    return {
      tree: tree,
      treeLinkList: treeLinkList
    };
  }

  @Action()
  public editPage(data: {
    id: string | number;
    pageName: string;
    description: string;
    parentPageId: string | number;
    customKeywords: string[];
    shouldRegenerateContent?: boolean;
  }) {
    let node = this.treeSiteMap.find((item) => item.id === data.id);
    const oldId = node.id;
    const oldName = node.text;
    const newName = data.pageName;
    let newId = oldId;
    // If the name changes, generate a new id (e.g. random + name)
    if (oldName !== newName) {
      newId = Math.floor(Math.random() * 1000) + '-' + newName;
      // Update parent references for all children
      this.treeSiteMap.forEach((n) => {
        if (n.parent === oldId) {
          n.parent = newId;
        }
      });
      // Update the node's own id
      node.id = newId;
    }
    node.text = newName;
    node.data.pageName = newName;
    node.data.description = data.description;
    node.data.customKeywords = data.customKeywords;
    node.parent =
      data.parentPageId !== null && data.parentPageId !== void 0
        ? data.parentPageId
        : this.SITEMAP_ROOT_INTERNAL;
    if (typeof data.shouldRegenerateContent !== 'undefined') {
      node.data.shouldRegenerateContent = data.shouldRegenerateContent;
    }

    let parentNode = this.treeSiteMap.find((item) => item.id === node.parent);
    if (parentNode) {
      parentNode.hasPageChildren = true;
    }

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  @Action()

  public addCustomPage(data: {
    pageName: string;
    description: string;
    parentPageId: string | number | ITreeNode;
    keywords: string[];
    // optional: insert after a specific node id (insert after its subtree)
    insertAfterId?: string | number | null;
    shouldRegenerateContent?: boolean;
  }) {
    let id = Math.floor(Math.random() * 1000) + '-' + data.pageName;

    const parentId: string | number =
      typeof data.parentPageId === 'object' && data.parentPageId !== null
        ? (data.parentPageId as ITreeNode).id
        : (data.parentPageId as string | number);

  let node = {
      id: id,
      parent: parentId,
      text: data.pageName,
      droppable: true,
  data: {
        path: '',
        description: data.description,
        pageName: data.pageName,
        keywords: [],
        deselectedKeywords: [],
        customKeywords: data.keywords || [],
  canDrag: true,
  shouldRegenerateContent: data.shouldRegenerateContent ?? false,
        canEdit: true,
        canDelete: true,
        isPageExternal: false,
        isPageDefault: false,
        isCategoryBasedPage: false,
        isCustomPage: true,
        type: 'custom',
        origin: 'custom' as const
      }
    };

    // Helper to find last index of a subtree (node + all its descendants)
    const findLastIndexOfSubtree = (rootId: string | number): number => {
      let lastIndex = this.treeSiteMap.findIndex((item) => item.id === rootId);
      if (lastIndex === -1) return -1;

      const children = this.treeSiteMap.filter((item) => item.parent === rootId);
      if (children.length === 0) return lastIndex;

      children.forEach((child) => {
        const childLast = findLastIndexOfSubtree(child.id);
        if (childLast > lastIndex) lastIndex = childLast;
      });

      return lastIndex;
    };

    // If insertAfterId provided, insert after that node's subtree
    if (data.insertAfterId != null) {
      const afterId: string | number =
        typeof data.insertAfterId === 'object' && data.insertAfterId !== null
          ? (data.insertAfterId as ITreeNode).id
          : (data.insertAfterId as string | number);

      const afterIndex = findLastIndexOfSubtree(afterId);
      if (afterIndex !== -1) {
        this.treeSiteMap.splice(afterIndex + 1, 0, node as ITreeNode);
      } else {
        this.treeSiteMap.push(node as ITreeNode);
      }
    } else {
      // Default: previous behavior - insert after last child of parent
      let parentNodeIndex = this.treeSiteMap.findIndex(
        (item) => item.id === parentId
      );

      if (parentNodeIndex !== -1) {
        // Find the last child of the parent node or the parent itself
        let positionToInsert = parentNodeIndex + 1;

        // Find all children of this parent recursively to insert after them
        const findLastChildIndex = (parentId: string | number): number => {
          let lastIndex = this.treeSiteMap.findIndex(
            (item) => item.id === parentId
          );
          let children = this.treeSiteMap.filter(
            (item) => item.parent === parentId
          );

          if (children.length > 0) {
            children.forEach((child) => {
              let childLastIndex = findLastChildIndex(child.id);
              if (childLastIndex > lastIndex) {
                lastIndex = childLastIndex;
              }
            });
          }

          return lastIndex;
        };

  positionToInsert = findLastChildIndex(parentId) + 1;

  // Insert the new node at the calculated position
  this.treeSiteMap.splice(positionToInsert, 0, node as ITreeNode);
      } else {
        // If parent not found, add at the end
  this.treeSiteMap.push(node as ITreeNode);
      }
    }

    // Update parent's hasPageChildren flag
    let parentNode = this.treeSiteMap.find((item) => item.id === parentId);
    if (parentNode) {
      parentNode.hasPageChildren = true;
    }

    // Update order for all siblings after adding a new page
    const siblings = this.treeSiteMap.filter(
      (n) => n.parent === parentId
    );
    siblings.forEach((sibling, index) => {
      sibling.data.order = index;
    });

    // @TODO - optimize this, the reason to do this is to trigger a re-render
    this.treeSiteMap = [...this.treeSiteMap];
  }

  @Action()
  public setSiteMapFething(value: boolean) {
    this.siteMapFething = value;
  }

  @Action()
  public setExternalSiteMapFething(value: boolean) {
    this.externalSiteMapFething = value;
  }

  @Action()
  public getSiteMapNodesForSessionStorage(): {
    siteMap: SiteMapWizardDataType;
  } {
    let sitemap = new Map();

    this.treeSiteMap.forEach((node) => {
      if (this.isTreeNodeExternal(node) === false) {
        // collect only the internal pages
        sitemap.set(node.id, {
          pageName: node.data.pageName,
          parent: node.parent,
          pagePath: node.data.path,
          description: node.data.description,
          isCustomPage: node.data.isCustomPage,
          type: node.data.type,
          keywords: node.data.keywords || [],
          pictures: [],
          section: [],
          areas: []
        });
      }
    });

    return {
      siteMap: sitemap
    };
  }

  @Action()
  public getDynamicPages(): ITreeNode[] {
    return this.treeSiteMap.filter((node) => node.data.type === 'dynamic');
  }

  @Action()
  public async fetchExternalSiteMap(
    websiteDomain: string,
    companyName: string,
    country: string
  ) {
    this.setExternalSiteMapFething(true);

    try {
      let extetnalSiteMap = await Api.getWebsiteData({
        website: websiteDomain,
        companyName,
        country
      });

      // this root node will not be printed,
      // it is used to make the children inside not droppable
      let rootNode: ISiteMapTreeData = {
        id: this.SITEMAP_ROOT_EXTERNAL,
        parent: null,
        canDrag: false,
        canEdit: false,
        canDelete: false,
        canHaveChildren: false,
        isPageDefault: false,
        isCategoryBasedPage: false,
        isDropdownPage: false,
        type: 'external',
        data: {
          path: '',
          description: '',
          pageName: '',
          keywords: []
        }
      };

      extetnalSiteMap.data.pages.unshift(rootNode);

      this.loadSiteMap(extetnalSiteMap.data.pages, 'external');
      this.oldWebsiteImages = extetnalSiteMap.data.images;
    } catch (error) {
      console.error('Error fetching external site map:', error);
    } finally {
      this.setExternalSiteMapFething(false);
    }
  }

  @Action()
  public async fetchSiteMap(data: SitemapParams) {
    this.setSiteMapFething(true);

    let sitemap = await Api.getSitemap(data);

    this.loadSiteMap(sitemap.data, 'internal');

    this.setSiteMapFething(false);

    this.populateTreeLinkedList(this.treeSiteMap, this.treeLinkList);
    this.treeLinkList.resetPointer();
  }

  @Action()
  public getSiteMapFinalTree(filter?: { excludeDefaultPages?: boolean }) {
    let finalTree = [];
    let recursive = (node: ITreeNode) => {
      if (filter && filter.excludeDefaultPages && node.data.isPageDefault) {
        return;
      }

      let keywords = [...node.data.customKeywords];

      if (node.data.keywords?.length > 0) {
        keywords = [...keywords, ...node.data.keywords];
      }

      if (node.data.deselectedKeywords?.length > 0) {
        // remove deselected keywords from keywords
        node.data.deselectedKeywords.forEach((keyword) => {
          let index = keywords.indexOf(keyword);

          if (index !== -1) {
            keywords.splice(index, 1);
          }
        });
      }

      finalTree.push({
        id: node.id,
        parent: node.parent,
        type: node.data.type,
        data: {
          path: node.data.path,
          description: node.data.description,
          pageName: node.text,
          keywords: toJS(keywords)
        }
      });

      let children = this.treeSiteMap.filter((item) => item.parent === node.id);

      children.forEach((child) => {
        recursive(child);
      });
    };

    this.treeSiteMap.forEach((node) => {
      if (node.parent === this.SITEMAP_ROOT_INTERNAL) {
        recursive(node);
      }
    });

    return finalTree;
  }

  isExternalTreeEmpty() {
    return (
      this.treeSiteMap.filter(
        (node) => node.parent === this.SITEMAP_ROOT_EXTERNAL
      ).length === 0
    );
  }

  @Action()
  public getNodeParentData(node: ITreeNode) {
    let nodeParents = [];

    const recur = (nodeId) => {
      if (!nodeId) return;

      let parent = this.treeSiteMap.find((item) => item.id === nodeId);
      if (parent) {
        nodeParents.push(parent.parent);
        recur(parent.parent);
      }
    };

    if (node.parent) {
      nodeParents.push(node.parent);
      recur(node.parent);
    }

    return nodeParents;
  }

  @Action()
  public checkForDuplicateNode(nodeData, editedPageNode = null) {
    const parent = nodeData?.parentPageId
      ? nodeData.parentPageId
      : this.SITEMAP_ROOT_INTERNAL;

    const siblings = this.treeSiteMap.filter((item) => item.parent === parent);

    const similarSibling = siblings.find((item) => {
      return (
        item.data.pageName.toLowerCase() === nodeData.pageName.toLowerCase() &&
        item.id !== editedPageNode
      );
    });

    if (similarSibling) {
      return true;
    }
  }

  getWizardData() {
    return this.siteMapWizardData.getWizardData();
  }

  getSessionKey(): string {
    return 'SiteMapModel';
  }
}

const model = new SiteMapModel();
WizardSessionData.addModel(model);
export default model;
